import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { galleryImages } from '../data/content';
import Icon from './Icons';
import Reveal from './Reveal';
import './Gallery.css';

/** Responzívna masonry galéria s lightboxom a lazy loadingom. */
export default function Gallery() {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const close = useCallback(() => setLightboxIndex(null), []);
  const step = useCallback(
    (direction) => {
      setLightboxIndex((index) =>
        index === null ? null : (index + direction + galleryImages.length) % galleryImages.length
      );
    },
    []
  );

  // Ovládanie klávesnicou: Esc zatvorí, šípky listujú.
  useEffect(() => {
    if (lightboxIndex === null) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowRight') step(1);
      if (event.key === 'ArrowLeft') step(-1);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [lightboxIndex, close, step]);

  return (
    <section className="section section--gray" id="galeria">
      <div className="container">
        <Reveal className="section-heading">
          <span className="eyebrow">Galéria</span>
          <h2>Pozri si ju zblízka</h2>
        </Reveal>

        <div className="gallery" role="list">
          {galleryImages.map((image, index) => (
            <Reveal key={image.src} delay={Math.min(index * 0.05, 0.3)}>
              <button
                type="button"
                role="listitem"
                className={`gallery__item ${image.tall ? 'gallery__item--tall' : ''}`}
                onClick={() => setLightboxIndex(index)}
                aria-label={`Zväčšiť fotografiu: ${image.alt}`}
              >
                <img src={image.src} alt={image.alt} loading="lazy" />
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {lightboxIndex !== null && (
          <motion.div
            className="lightbox"
            role="dialog"
            aria-modal="true"
            aria-label="Galéria fotografií"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
          >
            <button className="lightbox__close" onClick={close} aria-label="Zavrieť galériu">
              <Icon name="cross" />
            </button>
            <button
              className="lightbox__nav lightbox__nav--prev"
              onClick={(event) => { event.stopPropagation(); step(-1); }}
              aria-label="Predchádzajúca fotografia"
            >
              <Icon name="arrow" style={{ transform: 'rotate(180deg)' }} />
            </button>
            <motion.img
              key={lightboxIndex}
              src={galleryImages[lightboxIndex].src}
              alt={galleryImages[lightboxIndex].alt}
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.25 }}
              onClick={(event) => event.stopPropagation()}
            />
            <button
              className="lightbox__nav lightbox__nav--next"
              onClick={(event) => { event.stopPropagation(); step(1); }}
              aria-label="Ďalšia fotografia"
            >
              <Icon name="arrow" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
